import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Search, X, Plus, Package } from 'lucide-react';
import { API, withCreds, money } from './AdminUI';

// Cache titles of picked products so chips can render without refetching
const known = {};

export function useProductTitles(ids) {
  const [, force] = useState(0);
  useEffect(() => {
    const missing = ids.filter(id => !known[id]);
    if (!missing.length) return;
    Promise.all(missing.map(id => axios.get(`${API}/admin/products/${id}`, withCreds).then(r => r.data).catch(() => ({ id, title: '(deleted product)' }))))
      .then(list => { list.forEach(p => { known[p.id || p._id] = p; }); force(n => n + 1); });
  }, [ids.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  return known;
}

/**
 * Search-and-pick products. value: array of ids. With `withQuantity`, value is [{product_id, quantity}].
 * `filter` hides products that can't be picked (e.g. ones with options inside a bundle).
 */
export default function ProductPicker({ value = [], onChange, withQuantity = false, max, filter, placeholder = 'Search products to add…' }) {
  const ids = withQuantity ? value.map(v => v.product_id) : value;
  const titles = useProductTitles(ids);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      axios.get(`${API}/admin/products`, { params: { search: q, limit: 12 }, ...withCreds })
        .then(r => { r.data.products.forEach(p => { known[p.id] = p; }); setResults(r.data.products); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const add = (p) => {
    if (ids.includes(p.id) || (max && ids.length >= max)) return;
    onChange(withQuantity ? [...value, { product_id: p.id, quantity: 1 }] : [...value, p.id]);
  };
  const remove = (id) => onChange(withQuantity ? value.filter(v => v.product_id !== id) : value.filter(v => v !== id));
  const setQty = (id, qty) => onChange(value.map(v => (v.product_id === id ? { ...v, quantity: Math.max(1, qty) } : v)));
  const shown = results.filter(p => !ids.includes(p.id) && (!filter || filter(p)));

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {ids.map(id => {
          const p = titles[id];
          return (
            <div key={id} className="flex items-center gap-2 bg-ink/[0.04] border border-ink/10 rounded-lg pl-1 pr-2 py-1">
              <div className="w-7 h-7 rounded overflow-hidden bg-ink/[0.04] flex-shrink-0">
                {p?.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-ink/30 m-auto mt-1.5" />}
              </div>
              <span className="text-ink text-xs max-w-[160px] truncate">{p?.title || '…'}</span>
              {withQuantity && (
                <input type="number" min="1" value={value.find(v => v.product_id === id)?.quantity || 1}
                  onChange={e => setQty(id, parseInt(e.target.value) || 1)} title="Quantity"
                  className="w-12 bg-ink/[0.04] border border-ink/10 rounded px-1 py-0.5 text-ink text-xs" />
              )}
              <button type="button" onClick={() => remove(id)} className="text-ink/45 hover:text-red-600"><X className="w-3 h-3" /></button>
            </div>
          );
        })}
      </div>
      {(!max || ids.length < max) && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/45" />
          <input value={q} onChange={e => setQ(e.target.value)} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)}
            placeholder={placeholder}
            className="w-full bg-ink/[0.04] border border-ink/10 rounded-xl pl-10 pr-4 py-2 text-ink text-sm focus:outline-none focus:border-honey" />
          {open && shown.length > 0 && (
            <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-ink/10 rounded-xl shadow-xl">
              {shown.map(p => (
                <button type="button" key={p.id} onMouseDown={e => e.preventDefault()} onClick={() => add(p)}
                  className="w-full flex items-center gap-3 px-3 py-2 hover:bg-ink/[0.04] text-left">
                  <div className="w-8 h-8 rounded overflow-hidden bg-ink/[0.04] flex-shrink-0">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <span className="text-ink text-sm flex-1 truncate">{p.title}</span>
                  {p.sku && <span className="text-ink/45 text-xs font-mono">{p.sku}</span>}
                  <span className="text-ink/60 text-xs">{money(p.price)}</span>
                  <Plus className="w-4 h-4 text-honey-700" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CategoryPicker({ value = [], onChange }) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    axios.get(`${API}/admin/categories`, withCreds).then(r => setCats(r.data.categories)).catch(() => {});
  }, []);
  const toggle = (name) => onChange(value.includes(name) ? value.filter(v => v !== name) : [...value, name]);
  if (!cats.length) return <p className="text-ink/45 text-sm">No categories yet.</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {cats.map(c => (
        <button type="button" key={c.id} onClick={() => toggle(c.name)}
          className={`px-3 py-1.5 rounded-lg border text-sm ${value.includes(c.name) ? 'bg-honey/10 border-honey/40 text-honey-700' : 'bg-ink/[0.04] border-ink/10 text-ink/70 hover:text-ink'}`}>
          {c.name} <span className="text-xs opacity-60">{c.product_count}</span>
        </button>
      ))}
    </div>
  );
}
